/**
 * Create or edit a group -- the screen that makes the chart multi-level.
 *
 * The tree was always in the data model; there was simply no way to add a
 * level from here, so every chart stayed as flat as the template that
 * seeded it. The client's previous system let them nest, and an accountant
 * who cannot file Jeddah's customers separately from Riyadh's keeps that
 * breakdown somewhere else -- which is how a second set of books starts.
 *
 * Nature is settable only on creation, and only to the parent's. Changing
 * a group's nature afterwards would move every ledger beneath it, and
 * every posting ever made to them, from one side of the books to the
 * other -- while the trial balance still balanced.
 */
import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { createGroup, updateGroup } from '../api/endpoints'
import { errorMessage } from '../api/client'
import type { AccountGroupFlat } from '../api/types'
import { c } from '../theme'

const NATURES = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'] as const

interface Props {
  /** Present when editing; absent when creating. */
  group?: AccountGroupFlat
  /** The group the new one goes under. Absent means a top-level group. */
  parent?: { code: string; name_en: string; nature: string }
  /** Candidate parents for a move, already filtered to this nature. */
  parentOptions?: AccountGroupFlat[]
  onClose: () => void
  onSaved: () => void
}

export default function GroupDialog({
  group, parent, parentOptions, onClose, onSaved,
}: Props) {
  const { t } = useTranslation()
  const editing = !!group

  const [form, setForm] = useState({
    name_en: '', name_ar: '',
    // A child inherits its parent's nature, because the server refuses
    // anything else. Offering a dropdown that only ever has one valid
    // answer is a worse screen than showing the answer.
    nature: parent?.nature ?? 'ASSET',
    parent_code: parent?.code ?? '',
    affects_gross_profit: false,
  })
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!group) return
    setForm((f) => ({
      ...f, name_en: group.name_en, name_ar: group.name_ar ?? '',
      nature: group.nature,
      affects_gross_profit: group.affects_gross_profit,
      parent_code: parentOptions?.find((g) => g.id === group.parent_id)?.code ?? '',
    }))
  }, [group, parentOptions])

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        return updateGroup(group!.code, {
          name_en: form.name_en.trim(),
          name_ar: form.name_ar.trim() || null,
          parent_code: form.parent_code || null,
          affects_gross_profit: form.affects_gross_profit,
        })
      }
      return createGroup({
        name_en: form.name_en.trim(),
        name_ar: form.name_ar.trim() || undefined,
        nature: form.nature,
        parent_code: form.parent_code || null,
        affects_gross_profit: form.affects_gross_profit,
      })
    },
    onSuccess: () => { onSaved(); onClose() },
    onError: (e) => setError(errorMessage(e)),
  })

  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }))
  const ready = !!form.name_en.trim()
  const isPL = form.nature === 'INCOME' || form.nature === 'EXPENSE'

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {editing ? t('chart.editGroup') : t('chart.newGroup')}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}
          onClose={() => setError(null)}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField label={t('entry.name')} value={form.name_en} autoFocus
            onChange={(e) => set('name_en', e.target.value)} />
          <TextField label={`${t('entry.name')} (العربية)`} value={form.name_ar}
            onChange={(e) => set('name_ar', e.target.value)} />

          <Stack direction="row" spacing={2}>
            {/* On a new group the parent is where the user clicked, and
                not editable here -- they chose it by selecting it. */}
            {editing ? (
              <TextField select label={t('chart.under')} value={form.parent_code}
                onChange={(e) => set('parent_code', e.target.value)}
                helperText={t('chart.sameNatureOnly')} sx={{ flex: 1 }}>
                <MenuItem value="">{t('chart.topLevel')}</MenuItem>
                {(parentOptions ?? [])
                  .filter((g) => g.id !== group!.id)
                  .map((g) => (
                    <MenuItem key={g.id} value={g.code}>{g.name_en}</MenuItem>
                  ))}
              </TextField>
            ) : (
              <TextField label={t('chart.under')} sx={{ flex: 1 }} disabled
                value={parent?.name_en ?? t('chart.topLevel')} />
            )}
            <TextField select label={t('chart.nature')} value={form.nature}
              disabled={editing || !!parent}
              onChange={(e) => set('nature', e.target.value)}
              helperText={editing || parent
                ? t('chart.natureFixed') : undefined}
              sx={{ width: 170 }}>
              {NATURES.map((n) => (
                <MenuItem key={n} value={n}>{t(`nature.${n}`)}</MenuItem>
              ))}
            </TextField>
          </Stack>

          {/* Tally's Direct/Indirect split, which is what yields a gross
              profit line as well as a net one. Meaningless on a balance
              sheet group, so it is only offered where it applies. */}
          {isPL && (
            <>
              <FormControlLabel
                control={<Switch checked={form.affects_gross_profit}
                  onChange={(e) =>
                    set('affects_gross_profit', e.target.checked)} />}
                label={t('chart.grossProfitLabel')} />
              <Typography variant="caption" sx={{ color: c.inkMuted }}>
                {t('chart.grossProfitHint')}
              </Typography>
            </>
          )}

          {!editing && (
            <Typography variant="caption" sx={{ color: c.inkMuted }}>
              {t('chart.codeAutoHint')}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">{t('common.cancel')}</Button>
        <Button variant="contained" onClick={() => save.mutate()}
          disabled={!ready || save.isPending}>
          {save.isPending ? t('common.loading') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
